import { ArrowRight, CalendarDays, Truck, RefreshCw, BarChart3, Settings, Package } from 'lucide-react';
import { useLocation } from 'wouter';
import MarketingNav from '../components/marketing/MarketingNav';
import MarketingFooter from '../components/marketing/MarketingFooter';
import { usePageSEO } from '../utils/seo';

const ProductPage = () => {
    const [, setLocation] = useLocation();

    usePageSEO({
        title: 'Product Capabilities — BayWhiz Warehouse Dock Scheduling Software',
        description:
            'Explore BayWhiz dock scheduling capabilities: live warehouse schedule timelines, carrier self-service booking with PO validation, dock door assignment, check-in/checkout tracking, and dwell analytics.',
        canonicalPath: '/product',
    });

    return (
        <div className="min-h-screen bg-white text-slate-900 font-sans overflow-x-hidden">
            <MarketingNav variant="light" />

            {/* 1. PRODUCT HERO */}
            <section className="pt-16 sm:pt-20 pb-16 px-4 sm:px-6 text-center max-w-4xl mx-auto">
                <span className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-4 block">
                    The BayWhiz Platform
                </span>
                <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold mb-6 text-slate-900 tracking-tight leading-tight">
                    Everything your team needs to run the dock schedule.
                </h1>
                <p className="text-base sm:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed mb-10">
                    Manage carrier appointment requests, dock door assignments, check-ins, receiving reconciliation, and dwell time from one shared warehouse workspace.
                </p>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4">
                    <button
                        type="button"
                        onClick={() => setLocation('/request-trial')}
                        className="w-full sm:w-auto h-14 px-8 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold text-base transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer"
                    >
                        Request Free Trial
                        <ArrowRight size={18} />
                    </button>
                    <button
                        type="button"
                        onClick={() => setLocation('/how-it-works')}
                        className="w-full sm:w-auto h-14 px-8 rounded-xl bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold text-base transition-all cursor-pointer"
                    >
                        See How It Works
                    </button>
                </div>
                <p className="text-xs sm:text-sm text-slate-500 mt-5 font-medium">
                    30-day full-access trial &middot; No credit card required
                </p>
            </section>

            {/* 2. SHOW THE ACTUAL PRODUCT */}
            <section className="px-4 sm:px-6 pb-20">
                <div className="max-w-6xl mx-auto rounded-2xl overflow-hidden shadow-2xl border border-slate-200 bg-white">
                    <img
                        src="/landing/real-warehouse-schedule.png"
                        alt="BayWhiz live warehouse dock schedule showing assigned dock doors D1 through D4 and pending approvals"
                        className="w-full h-auto block"
                        loading="eager"
                        width={1440}
                        height={820}
                    />
                </div>
            </section>

            {/* 3. CORE WORKFLOW — SCHEDULE & APPROVALS */}
            <section className="py-20 px-4 sm:px-6 bg-slate-50 border-t border-slate-200">
                <div className="max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <div className="order-2 md:order-1 rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-white">
                            <img
                                src="/landing/real-appointment-approval.png"
                                alt="BayWhiz Awaiting Approval queue and dock timeline"
                                className="w-full h-auto block"
                                loading="lazy"
                            />
                        </div>
                        <div className="order-1 md:order-2">
                            <div className="w-12 h-12 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center mb-6">
                                <CalendarDays size={24} />
                            </div>
                            <h2 className="text-2xl sm:text-4xl font-bold mb-6 text-slate-900 leading-tight">
                                See the whole dock schedule in one place.
                            </h2>
                            <p className="text-base sm:text-lg text-slate-600 mb-8 leading-relaxed">
                                Your team can see what is due, what has arrived, which dock door is assigned, and what is awaiting approval without piecing the shift together from phone calls and spreadsheets.
                            </p>
                            <ul className="space-y-4">
                                {[
                                    'Scheduled appointments organized by time window and dock door',
                                    'Today timeline, 7-Day board, and Month calendar views',
                                    'Awaiting Approval queue with one-click Approve, Revise, or Decline',
                                    'One-click dock door assignment and reassignment with conflict checks',
                                ].map((item) => (
                                    <li key={item} className="flex items-start gap-3 text-slate-700">
                                        <div className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center shrink-0 mt-0.5">
                                            <div className="w-2 h-2 rounded-full bg-teal-600" />
                                        </div>
                                        <span className="font-medium text-[15px]">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* 4. CORE WORKFLOW — CARRIERS */}
            <section className="py-20 px-4 sm:px-6 bg-white border-t border-slate-200">
                <div className="max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <div>
                            <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center mb-6">
                                <Truck size={24} />
                            </div>
                            <h2 className="text-2xl sm:text-4xl font-bold mb-6 text-slate-900 leading-tight">
                                Give carriers a structured way to request appointments.
                            </h2>
                            <p className="text-base sm:text-lg text-slate-600 mb-8 leading-relaxed">
                                Eliminate back-and-forth scheduling emails while keeping the warehouse in complete control of slot capacity and PO verification.
                            </p>
                            <div className="space-y-6">
                                <div className="pl-6 border-l-2 border-slate-200 relative">
                                    <div className="absolute w-3 h-3 bg-white border-2 border-slate-300 rounded-full -left-[7px] top-1" />
                                    <h3 className="font-bold text-slate-900 mb-1">PO Validation &amp; Slot Request</h3>
                                    <p className="text-slate-600 text-sm">
                                        Carriers validate an open PO or load number on your facility booking link and choose from available time slots.
                                    </p>
                                </div>
                                <div className="pl-6 border-l-2 border-slate-200 relative">
                                    <div className="absolute w-3 h-3 bg-white border-2 border-slate-300 rounded-full -left-[7px] top-1" />
                                    <h3 className="font-bold text-slate-900 mb-1">Warehouse Review</h3>
                                    <p className="text-slate-600 text-sm">
                                        Your receiving team reviews carrier, equipment, and pallet details before confirming the slot.
                                    </p>
                                </div>
                                <div className="pl-6 border-l-2 border-teal-500 relative">
                                    <div className="absolute w-3 h-3 bg-white border-2 border-teal-500 rounded-full -left-[7px] top-1" />
                                    <h3 className="font-bold text-teal-800 mb-1">Schedule Confirmation &amp; Claim Token</h3>
                                    <p className="text-slate-600 text-sm">
                                        Approved bookings appear on your dock board and provide carriers a signed claim token to manage or cancel their booking.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-white">
                            <img
                                src="/landing/real-carrier-booking.png"
                                alt="BayWhiz carrier self-service booking portal showing PO validation and time slot selection"
                                className="w-full h-auto block"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* 5. CHECK-IN, CHECKOUT & RECEIVING CONTEXT */}
            <section className="py-20 px-4 sm:px-6 bg-slate-50 border-t border-slate-200">
                <div className="max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <div className="order-2 md:order-1 rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-white">
                            <img
                                src="/landing/real-checkin-checkout.png"
                                alt="BayWhiz Checked-In appointment execution dialog showing dock dispatch and PO receiving reconciliation"
                                className="w-full h-auto block"
                                loading="lazy"
                            />
                        </div>
                        <div className="order-1 md:order-2">
                            <div className="w-12 h-12 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center mb-6">
                                <Package size={24} />
                            </div>
                            <h2 className="text-2xl sm:text-4xl font-bold mb-6 text-slate-900 leading-tight">
                                Keep PO context and check-in status tied to the appointment.
                            </h2>
                            <p className="text-base sm:text-lg text-slate-600 mb-8 leading-relaxed">
                                Keep PO numbers, expected pallet counts, carrier equipment, and live check-in timestamps together so the receiving team has complete context when a truck arrives.
                            </p>
                            <ul className="space-y-4">
                                {[
                                    'Link Purchase Orders directly to inbound appointments',
                                    'Track arrival, check-in, dock dispatch, and checkout timestamps',
                                    'Verify carrier MC #, truck #, trailer #, and expected pallet count at the door',
                                ].map((item) => (
                                    <li key={item} className="flex items-start gap-3 text-slate-700">
                                        <div className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center shrink-0 mt-0.5">
                                            <div className="w-2 h-2 rounded-full bg-teal-600" />
                                        </div>
                                        <span className="font-medium text-[15px]">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* 6. CORE WORKFLOW — DOCK REASSIGNMENT & AUDIT HISTORY */}
            <section className="py-20 px-4 sm:px-6 bg-slate-900 text-white">
                <div className="max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <div className="order-2 md:order-1 rounded-2xl overflow-hidden shadow-2xl border border-slate-700 bg-white">
                            <img
                                src="/landing/real-appointment-audit-history.png"
                                alt="BayWhiz appointment audit trail showing original approval, dock reassignment D1 to D2, and cancellation history"
                                className="w-full h-auto block"
                                loading="lazy"
                            />
                        </div>
                        <div className="order-1 md:order-2">
                            <div className="w-12 h-12 rounded-xl bg-slate-800 text-teal-400 flex items-center justify-center mb-6 border border-slate-700">
                                <RefreshCw size={24} />
                            </div>
                            <h2 className="text-2xl sm:text-4xl font-bold mb-6 text-white leading-tight">
                                Reassign docks and handle cancellations without losing audit history.
                            </h2>
                            <p className="text-base sm:text-lg text-slate-300 mb-8 leading-relaxed">
                                When yard priorities shift or a carrier cancels, BayWhiz updates door availability immediately while preserving the full chronological history of approvals and dock assignments.
                            </p>
                            <ul className="space-y-4">
                                {[
                                    'Reassign dock doors with automatic conflict checks',
                                    'Release dock doors automatically when an approved booking is cancelled',
                                    'Preserve who approved the appointment, when, and which dock was assigned',
                                    'Inspect full chronological activity history for every appointment',
                                ].map((item) => (
                                    <li key={item} className="flex items-start gap-3 text-slate-200">
                                        <div className="w-6 h-6 rounded-full bg-slate-800 text-teal-400 flex items-center justify-center shrink-0 mt-0.5 border border-slate-700">
                                            <div className="w-2 h-2 rounded-full bg-teal-400" />
                                        </div>
                                        <span className="font-medium text-[15px]">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* 7. OPERATIONAL VISIBILITY / ANALYTICS */}
            <section className="py-20 px-4 sm:px-6 bg-white">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-14 max-w-3xl mx-auto">
                        <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center mx-auto mb-6 border border-teal-200">
                            <BarChart3 size={24} />
                        </div>
                        <h2 className="text-2xl sm:text-4xl font-bold mb-4 text-slate-900">
                            See what is slowing the operation down.
                        </h2>
                        <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                            Use timestamped check-in and completion data to monitor average dwell time, detention exposure, and per-dock turnaround.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 mb-12">
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                            <h3 className="font-bold text-slate-900 text-lg mb-2">Dwell &amp; Detention Analysis</h3>
                            <p className="text-slate-600 text-sm leading-relaxed">
                                Track elapsed time between truck check-in and completion against your facility&rsquo;s configurable grace period.
                            </p>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                            <h3 className="font-bold text-slate-900 text-lg mb-2">Per-Dock Performance</h3>
                            <p className="text-slate-600 text-sm leading-relaxed">
                                Compare load counts and average dwell minutes across individual dock doors over 7, 14, or 30 days.
                            </p>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                            <h3 className="font-bold text-slate-900 text-lg mb-2">Schedule CSV &amp; Printable PDF Exports</h3>
                            <p className="text-slate-600 text-sm leading-relaxed">
                                Export appointment schedules to CSV and generate printable PDF dwell and detention reports for shift reviews, carrier scorecards, or detention dispute documentation.
                            </p>
                        </div>
                    </div>

                    <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-white">
                        <img
                            src="/landing/real-dwell-reporting.png"
                            alt="BayWhiz Dwell & Detention Analysis dashboard"
                            className="w-full h-auto block"
                            loading="lazy"
                        />
                    </div>
                </div>
            </section>

            {/* 8. SIMPLE SETUP */}
            <section className="py-20 px-4 sm:px-6 bg-slate-50 border-t border-slate-200">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 text-slate-700 flex items-center justify-center mx-auto mb-6 shadow-sm">
                        <Settings size={24} />
                    </div>
                    <h2 className="text-2xl sm:text-4xl font-bold mb-4 text-slate-900">
                        Start with the dock schedule. Keep the systems you already use.
                    </h2>
                    <p className="text-base sm:text-lg text-slate-600 mb-10 leading-relaxed">
                        BayWhiz works as a dedicated dock scheduling layer alongside your existing warehouse workflows—create POs directly or import them via CSV without replacing your ERP or WMS.
                    </p>

                    <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-6">
                        <div className="flex items-center gap-4 bg-white px-6 py-4 rounded-xl shadow-sm border border-slate-200 w-full md:w-auto">
                            <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-bold text-sm">
                                1
                            </div>
                            <span className="font-medium text-slate-800">Configure your docks &amp; hours</span>
                        </div>
                        <ArrowRight className="text-slate-400 hidden md:block" size={22} />
                        <div className="flex items-center gap-4 bg-white px-6 py-4 rounded-xl shadow-sm border border-slate-200 w-full md:w-auto">
                            <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-bold text-sm">
                                2
                            </div>
                            <span className="font-medium text-slate-800">Add POs &amp; share booking link</span>
                        </div>
                        <ArrowRight className="text-slate-400 hidden md:block" size={22} />
                        <div className="flex items-center gap-4 bg-white px-6 py-4 rounded-xl shadow-sm border border-teal-500 w-full md:w-auto">
                            <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center font-bold text-sm">
                                3
                            </div>
                            <span className="font-bold text-slate-900">Approve &amp; track arrivals</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* 9. FINAL CTA */}
            <section className="py-20 px-4 sm:px-6 bg-white border-t border-slate-200">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl sm:text-5xl font-bold mb-6 text-slate-900 tracking-tight">
                        Ready to get your dock schedule out of spreadsheets?
                    </h2>
                    <p className="text-base sm:text-xl text-slate-600 mb-10 max-w-2xl mx-auto">
                        Start with a 30-day full-access trial and move your warehouse dock appointments into one shared schedule.
                    </p>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4">
                        <button
                            type="button"
                            onClick={() => setLocation('/request-trial')}
                            className="h-14 px-8 rounded-xl bg-teal-600 hover:bg-teal-700 text-white font-semibold text-base transition-all shadow-md w-full sm:w-auto flex items-center justify-center gap-2 cursor-pointer"
                        >
                            Request Trial Access
                            <ArrowRight size={18} />
                        </button>
                        <button
                            type="button"
                            onClick={() => setLocation('/pricing')}
                            className="h-14 px-8 rounded-xl bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 font-semibold text-base transition-all w-full sm:w-auto flex items-center justify-center cursor-pointer"
                        >
                            View Pricing
                        </button>
                    </div>
                    <p className="mt-5 text-xs sm:text-sm text-slate-500 font-medium">
                        30-day full-access trial &middot; No credit card required &middot; Plans start at $99 USD/month per facility
                    </p>
                </div>
            </section>

            <MarketingFooter variant="light" />
        </div>
    );
};

export default ProductPage;
